/**
 * Website-template (`template_components`) unpublished session.
 *
 * - New Add rows insert as `draft` (public `shouldShowToViewer` hides them).
 * - Publish promotes drafts → `active` and hard-deletes deferred removals.
 * - Discard / abandoned refresh hard-deletes session-created drafts (+ links).
 * - Already-live removals are deferred until Publish (public stays unchanged).
 *
 * Link in Bio (`stayuptodate_components`) writes immediately — this file
 * is website-template only and must not touch stayuptodate_components.
 */
import { supabase } from "../../../config/supabaseClient";
import {
  TEMPLATE_COMPONENTS_TABLE,
  WEBSITE_TEMPLATE_SURFACE,
  getComponentsTableName,
  getUserLinksTypeForSurface,
} from "../../../Utils/StayUpToDate/componentSurface";

const createdIds = new Set<string>();
const deletedIds = new Set<string>();
/** Pending column patches for already-live rows (flushed on Publish). */
const dirtyPatches = new Map<string, Record<string, unknown>>();
/**
 * Bumped on Discard so an in-flight template_components write cannot land
 * after the revert. Callers capture the value before they await.
 */
let writeEpoch = 0;
/** Original columns for rows written immediately during the open edit. */
const rowBaselines = new Map<string, Record<string, unknown>>();
/** Track links created during an unpublished session (discard cleanup). */
const createdUserLinkIds = new Set<string>();
/** Add-flow session keys (`add-links-*`) before a component row exists. */
const linkSessionCollections = new Set<string>();

let storageUserId = "";
let onSessionDirty: (() => void) | null = null;

function rowId(value: unknown): string {
  return value == null ? "" : String(value).trim();
}

/** Fired whenever the website-template session has unpublished work. */
export const MTE_WT_SESSION_DIRTY = "mte-wt-session-dirty";

function markSessionDirty() {
  onSessionDirty?.();
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(MTE_WT_SESSION_DIRTY));
}
/** Register dirty callback (enables Publish when session drafts/edits/removes exist). */
export function beginWebsiteTemplatePersistGate(cb: () => void) {
  onSessionDirty = cb;
}

export function endWebsiteTemplatePersistGate() {
  onSessionDirty = null;
}

/** True when Add/Edit/Remove work is waiting for Publish. */
export function isWebsiteTemplateSessionDirty(): boolean {
  return (
    createdIds.size > 0 ||
    deletedIds.size > 0 ||
    dirtyPatches.size > 0 ||
    createdUserLinkIds.size > 0 ||
    linkSessionCollections.size > 0
  );
}

/** Link rows already written (reorder) still need Publish enabled. */
export function noteWebsiteTemplateSessionDirty() {
  markSessionDirty();
}

function storageKey(userId: string): string {
  return `mte-wt-session:${String(userId || "").trim()}`;
}

function persistSession() {
  if (typeof window === "undefined" || !storageUserId) return;
  try {
    const dirty: Record<string, Record<string, unknown>> = {};
    dirtyPatches.forEach((patch, id) => {
      dirty[id] = patch;
    });
    window.sessionStorage.setItem(
      storageKey(storageUserId),
      JSON.stringify({
        created: [...createdIds],
        deleted: [...deletedIds],
        dirty,
        linkIds: [...createdUserLinkIds],
        linkSessions: [...linkSessionCollections],
      }),
    );
  } catch {
    /* private mode / quota — in-memory still works for the tab */
  }
}

function loadSession(userId: string) {
  storageUserId = String(userId || "").trim();
  createdIds.clear();
  deletedIds.clear();
  dirtyPatches.clear();
  if (typeof window === "undefined" || !storageUserId) return;
  try {
    const raw = window.sessionStorage.getItem(storageKey(storageUserId));
    if (!raw) return;
    const parsed = JSON.parse(raw) as {
      created?: unknown[];
      deleted?: unknown[];
      dirty?: Record<string, Record<string, unknown>>;
      linkIds?: unknown[];
      linkSessions?: unknown[];
    };
    for (const id of parsed.created || []) {
      const key = rowId(id);
      if (key) createdIds.add(key);
    }
    for (const id of parsed.deleted || []) {
      const key = rowId(id);
      if (key) deletedIds.add(key);
    }
    if (parsed.dirty && typeof parsed.dirty === "object") {
      for (const [id, patch] of Object.entries(parsed.dirty)) {
        const key = rowId(id);
        if (key && patch && typeof patch === "object") {
          dirtyPatches.set(key, { ...patch });
        }
      }
    }
    for (const id of parsed.linkIds || []) {
      const key = rowId(id);
      if (key) createdUserLinkIds.add(key);
    }
    for (const session of parsed.linkSessions || []) {
      const key = String(session || "").trim();
      if (key) linkSessionCollections.add(key);
    }
  } catch {
    /* ignore corrupt storage */
  }
}

function clearStorage() {
  if (typeof window === "undefined" || !storageUserId) return;
  try {
    window.sessionStorage.removeItem(storageKey(storageUserId));
  } catch {
    /* ignore */
  }
}

/** Bind / hydrate session tracking for this editor user. */
export function bindWebsiteTemplateSession(userId: string) {
  const uid = String(userId || "").trim();
  if (!uid) return;
  if (uid !== storageUserId) loadSession(uid);
  else storageUserId = uid;
}

export function noteWebsiteTemplateCreatedId(id: unknown) {
  const key = rowId(id);
  if (!key) return;
  createdIds.add(key);
  deletedIds.delete(key);
  dirtyPatches.delete(key);
  persistSession();
  markSessionDirty();
}

export function forgetWebsiteTemplateCreatedId(id: unknown) {
  const key = rowId(id);
  if (!key) return;
  createdIds.delete(key);
  persistSession();
}

export function clearWebsiteTemplateCreatedIds() {
  createdIds.clear();
  deletedIds.clear();
  dirtyPatches.clear();
  createdUserLinkIds.clear();
  linkSessionCollections.clear();
  clearStorage();
}

export function getWebsiteTemplateCreatedIds(): string[] {
  return [...createdIds];
}

export function isWebsiteTemplateSessionCreated(id: unknown): boolean {
  const key = rowId(id);
  return Boolean(key) && createdIds.has(key);
}

export function noteWebsiteTemplateDeletedId(id: unknown) {
  const key = rowId(id);
  if (!key) return;
  if (createdIds.has(key)) {
    createdIds.delete(key);
    dirtyPatches.delete(key);
    persistSession();
    markSessionDirty();
    return;
  }
  deletedIds.add(key);
  dirtyPatches.delete(key);
  persistSession();
  markSessionDirty();
}

export function getWebsiteTemplateDeletedIds(): string[] {
  return [...deletedIds];
}

/** Unpublished column patch for a live row — overlay on DB reads so refreshes don't revert edits. */
export function getWebsiteTemplateDirtyPatch(
  componentId: unknown,
): Record<string, unknown> | undefined {
  const key = rowId(componentId);
  return key ? dirtyPatches.get(key) : undefined;
}

/** True when an already-live website row should not write DB until Publish. */
export function shouldDeferWebsiteTemplateWrite(componentId?: unknown): boolean {
  const key = rowId(componentId);
  if (!key) return false;
  // Session drafts are invisible publicly — safe to write immediately.
  if (createdIds.has(key)) return false;
  return true;
}

export function noteWebsiteTemplateDirtyPatch(
  componentId: unknown,
  patch: Record<string, unknown>,
) {
  const key = rowId(componentId);
  if (!key || !patch || Object.keys(patch).length === 0) return;
  if (createdIds.has(key)) return;
  const prev = dirtyPatches.get(key) || {};
  dirtyPatches.set(key, { ...prev, ...patch });
  persistSession();
  markSessionDirty();
}

/** Merge into an existing image/publish patch without enabling Publish. */
export function patchWebsiteTemplateDirtyPatchSilent(
  componentId: unknown,
  patch: Record<string, unknown>,
) {
  const key = rowId(componentId);
  if (!key || !patch || Object.keys(patch).length === 0) return;
  const prev = dirtyPatches.get(key);
  if (!prev) return;
  dirtyPatches.set(key, { ...prev, ...patch });
  persistSession();
}

/** Drop deferred live-row patches without deleting session-created drafts. */
export function clearWebsiteTemplateDirtyPatches() {
  if (dirtyPatches.size === 0) return;
  dirtyPatches.clear();
  persistSession();
}

export function websiteTemplateWriteEpoch(): number {
  return writeEpoch;
}

/** Invalidate in-flight edits. Call at the start of Discard, before revert. */
export function bumpWebsiteTemplateWriteEpoch(): number {
  writeEpoch += 1;
  return writeEpoch;
}

export function captureWebsiteTemplateDeletedIds(): string[] {
  return [...deletedIds];
}

/** Put unpublished removals back to the set captured when this edit opened. */
export function restoreWebsiteTemplateDeletedIds(ids: string[]) {
  deletedIds.clear();
  for (const id of ids) {
    const key = rowId(id);
    if (key) deletedIds.add(key);
  }
  persistSession();
}

export function clearWebsiteTemplateRowBaselines() {
  rowBaselines.clear();
}

async function readRowBaselineColumns(
  userId: string,
  id: string,
  columns: string[],
): Promise<Record<string, unknown> | null> {
  const cols = columns.filter(Boolean);
  if (!cols.length) return {};
  const { data, error } = await supabase
    .from(TEMPLATE_COMPONENTS_TABLE)
    .select(cols.join(","))
    .eq("user_id", userId)
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  if (!data || typeof data !== "object") return null;
  const row = data as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of cols) out[key] = row[key];
  return out;
}

/**
 * Immediate template_components write with a Discard baseline.
 * Returns false when Discard won the race (row left at the baseline).
 */
async function writeTemplateComponentNow(
  userId: string,
  id: string,
  patch: Record<string, unknown>,
  epoch: number,
): Promise<boolean> {
  const keys = Object.keys(patch);
  let baseline = rowBaselines.get(id);
  const missing = keys.filter((key) => !baseline || !(key in baseline));
  if (missing.length) {
    const read = await readRowBaselineColumns(userId, id, missing);
    if (epoch !== writeEpoch) return false;
    if (read) {
      baseline = { ...(baseline || {}), ...read };
      rowBaselines.set(id, baseline);
    }
  }
  if (epoch !== writeEpoch) return false;
  const { error } = await supabase
    .from(TEMPLATE_COMPONENTS_TABLE)
    .update(patch)
    .eq("user_id", userId)
    .eq("id", id);
  if (epoch !== writeEpoch) {
    if (baseline && Object.keys(baseline).length) {
      await supabase
        .from(TEMPLATE_COMPONENTS_TABLE)
        .update(baseline)
        .eq("user_id", userId)
        .eq("id", id);
    }
    return false;
  }
  if (error) throw error;
  return true;
}

/** Write session-draft rows back to the values from when this edit started. */
export async function revertWebsiteTemplateRowBaselines(
  userId?: string,
): Promise<void> {
  const uid = String(userId || "").trim() || storageUserId;
  const entries = [...rowBaselines.entries()];
  rowBaselines.clear();
  if (!uid || entries.length === 0) return;
  await Promise.all(
    entries.map(async ([id, cols]) => {
      if (!cols || Object.keys(cols).length === 0) return;
      await supabase
        .from(TEMPLATE_COMPONENTS_TABLE)
        .update(cols)
        .eq("user_id", uid)
        .eq("id", id);
    }),
  );
}

/**
 * Compat for callers that still import queue* APIs (highlights / content edits).
 * Draft rows write immediately; live rows defer until Publish.
 */
export async function queueWebsiteTemplateComponentUpdate(
  userId: string,
  componentId: unknown,
  patch: Record<string, unknown>,
  epochAtStart?: number,
) {
  const epoch = epochAtStart ?? writeEpoch;
  const uid = String(userId || "").trim() || storageUserId;
  const id = rowId(componentId);
  if (!id || !patch || Object.keys(patch).length === 0) return;
  if (!shouldDeferWebsiteTemplateWrite(id)) {
    if (!uid) return;
    await writeTemplateComponentNow(uid, id, patch, epoch);
    // Session drafts still count as unpublished until Publish.
    markSessionDirty();
    return;
  }
  if (epoch !== writeEpoch) return;
  noteWebsiteTemplateDirtyPatch(id, patch);
}

/** Immediate write (not deferred) that Discard can roll back. */
export async function writeWebsiteTemplateComponentNow(
  userId: string,
  componentId: unknown,
  patch: Record<string, unknown>,
): Promise<boolean> {
  const epoch = writeEpoch;
  const uid = String(userId || "").trim() || storageUserId;
  const id = rowId(componentId);
  if (!uid || !id || !patch || Object.keys(patch).length === 0) return false;
  const ok = await writeTemplateComponentNow(uid, id, patch, epoch);
  if (ok) markSessionDirty();
  return ok;
}

/** Compat: same rules as editor Remove (hard-delete draft / defer live). */
export async function queueWebsiteTemplateComponentDelete(
  userId: string,
  componentId: unknown,
) {
  const uid = String(userId || "").trim() || storageUserId;
  const id = rowId(componentId);
  if (!id) return;
  if (createdIds.has(id)) {
    await hardDeleteWebsiteTemplateComponentRow(uid, id);
    return;
  }
  noteWebsiteTemplateDeletedId(id);
}

/**
 * Soft-delete active `user_links` for a collection that has no owning
 * `links-section` row. Used when re-opening Add My Links after a Remove that
 * left orphan links under the default title (or an abandoned Add draft).
 *
 * `from` selects the surface: website-template → `template_components` +
 * `user_links.type = website-template`; landing-page → stayuptodate + landing-page.
 */
export async function purgeUnownedWebsiteUserLinks(
  userId: string,
  collectionName: string,
  from: string = WEBSITE_TEMPLATE_SURFACE,
): Promise<boolean> {
  const uid = String(userId || "").trim();
  const collection = String(collectionName || "").trim();
  if (!uid || !collection) return false;
  const linksType = getUserLinksTypeForSurface(from);
  const componentsTable =
    from === WEBSITE_TEMPLATE_SURFACE
      ? TEMPLATE_COMPONENTS_TABLE
      : getComponentsTableName(from);

  const { data: owners } = await supabase
    .from(componentsTable)
    .select("id")
    .eq("user_id", uid)
    .eq("type", "links-section")
    .eq("name", collection)
    .neq("status", "deleted")
    .limit(1);
  if (owners && owners.length > 0) return false;

  const { data: orphans } = await supabase
    .from("user_links")
    .select("id")
    .eq("user_id", uid)
    .eq("type", linksType)
    .eq("collection_name", collection)
    .eq("status", "active")
    .limit(1);
  if (!orphans?.length) return false;

  await supabase
    .from("user_links")
    .delete()
    .eq("user_id", uid)
    .eq("type", linksType)
    .eq("collection_name", collection);
  return true;
}

async function hardDeleteUserLinksForComponentIds(
  userId: string,
  ids: string[],
) {
  if (!ids.length) return;
  const linksType = getUserLinksTypeForSurface(WEBSITE_TEMPLATE_SURFACE);
  for (const componentId of ids) {
    const id = String(componentId || "").trim();
    if (!id) continue;
    await supabase
      .from("user_links")
      .delete()
      .eq("user_id", userId)
      .eq("type", linksType)
      .eq("parent_id", id);
  }
  const { data: linkSections } = await supabase
    .from(TEMPLATE_COMPONENTS_TABLE)
    .select("id, name, type")
    .eq("user_id", userId)
    .in("id", ids)
    .eq("type", "links-section");
  for (const row of linkSections || []) {
    const collection = String(row.name || "").trim();
    if (!collection) continue;
    await supabase
      .from("user_links")
      .delete()
      .eq("user_id", userId)
      .eq("type", linksType)
      .eq("collection_name", collection)
      .is("parent_id", null);
  }
}

async function hardDeleteTemplateComponentIds(userId: string, ids: string[]) {
  if (!ids.length) return;
  await hardDeleteUserLinksForComponentIds(userId, ids);
  await supabase
    .from(TEMPLATE_COMPONENTS_TABLE)
    .delete()
    .eq("user_id", userId)
    .in("id", ids);
}

/** Hard-delete one website `template_components` row (+ links if links-section). */
export async function hardDeleteWebsiteTemplateComponentRow(
  userId: string,
  rowIdValue: string,
) {
  const uid = String(userId || "").trim();
  const id = rowId(rowIdValue);
  if (!uid || !id) return;
  await hardDeleteTemplateComponentIds(uid, [id]);
  createdIds.delete(id);
  deletedIds.delete(id);
  dirtyPatches.delete(id);
  persistSession();
}

async function hardDeleteTrackedUserLinks(userId: string) {
  const uid = String(userId || "").trim();
  if (!uid) {
    createdUserLinkIds.clear();
    linkSessionCollections.clear();
    return;
  }
  const ids = [...createdUserLinkIds];
  if (ids.length) {
    await supabase
      .from("user_links")
      .delete()
      .eq("user_id", uid)
      .in("id", ids);
  }
  for (const session of linkSessionCollections) {
    await supabase
      .from("user_links")
      .delete()
      .eq("user_id", uid)
      .eq("collection_name", session);
  }
  createdUserLinkIds.clear();
  linkSessionCollections.clear();
}

export function noteWebsiteUserLinkCreatedId(id: unknown) {
  const key = rowId(id);
  if (!key) return;
  createdUserLinkIds.add(key);
  persistSession();
  markSessionDirty();
}

/** Track an Add My Links configure session key for discard cleanup. */
export function noteWebsiteUserLinkSessionCollection(sessionKey: unknown) {
  const key = String(sessionKey || "").trim();
  if (!key) return;
  linkSessionCollections.add(key);
  persistSession();
  markSessionDirty();
}

export function forgetWebsiteUserLinkSessionCollection(sessionKey: unknown) {
  const key = String(sessionKey || "").trim();
  if (!key) return;
  linkSessionCollections.delete(key);
  persistSession();
}

/**
 * Link in Bio "Add to page" binds these rows to a live section. Drop them from
 * the website draft-delete set so a refresh does not wipe the links.
 */
export function releaseWebsiteUserLinkCreatedIds(ids: Iterable<unknown>) {
  let changed = false;
  for (const id of ids) {
    const key = rowId(id);
    if (key && createdUserLinkIds.delete(key)) changed = true;
  }
  if (changed) persistSession();
}

/** Hard-delete links staged during an Add My Links configure session. */
export async function discardWebsiteUserLinksForSession(
  userId: string,
  sessionKey?: string,
  from?: string | null,
) {
  bindWebsiteTemplateSession(userId);
  const uid = String(userId || "").trim();
  const session = String(sessionKey || "").trim();
  if (!uid) return;
  if (session) {
    await supabase
      .from("user_links")
      .delete()
      .eq("user_id", uid)
      .eq("collection_name", session);
    linkSessionCollections.delete(session);
    persistSession();
    return;
  }
  await hardDeleteTrackedUserLinks(uid);
  persistSession();
}

/**
 * Discard / abandoned tab refresh: hard-delete unpublished session creates.
 * Pending deletes/edits of already-live rows are dropped (public unchanged).
 */
export async function discardWebsiteTemplateSession(userId: string) {
  bindWebsiteTemplateSession(userId);
  const ids = [...createdIds];
  createdIds.clear();
  deletedIds.clear();
  dirtyPatches.clear();
  const uid = String(userId || "").trim();
  await hardDeleteTrackedUserLinks(uid);
  clearStorage();
  if (!uid || ids.length === 0) return;
  await hardDeleteTemplateComponentIds(uid, ids);
}

/** True when this document load was a browser refresh/reload. */
export function isBrowserReloadNavigation(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const nav = performance.getEntriesByType(
      "navigation",
    )[0] as PerformanceNavigationTiming | undefined;
    return nav?.type === "reload";
  } catch {
    return false;
  }
}

/** Unpublished tweaks/creator drafts (survives browser refresh for Discard/Publish). */
export type WebsiteUnpublishedDraft = {
  tweaks: Record<string, unknown>;
  creatorData: Record<string, unknown>;
};

function unpublishedDraftKey(userId: string) {
  return `mte-unpublished-draft:${String(userId || "").trim()}`;
}

/** Persist in-memory website edits so refresh can restore + prompt Discard/Publish. */
export function saveWebsiteUnpublishedDraft(
  userId: string,
  draft: { tweaks: unknown; creatorData: unknown },
) {
  const uid = String(userId || "").trim();
  if (typeof window === "undefined" || !uid) return;
  try {
    window.sessionStorage.setItem(
      unpublishedDraftKey(uid),
      JSON.stringify({
        tweaks: draft.tweaks ?? {},
        creatorData: draft.creatorData ?? {},
      }),
    );
  } catch {
    /* private mode / quota */
  }
}

export function loadWebsiteUnpublishedDraft(
  userId: string,
): WebsiteUnpublishedDraft | null {
  const uid = String(userId || "").trim();
  if (typeof window === "undefined" || !uid) return null;
  try {
    const raw = window.sessionStorage.getItem(unpublishedDraftKey(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      tweaks?: unknown;
      creatorData?: unknown;
    };
    if (!parsed?.tweaks || typeof parsed.tweaks !== "object") return null;
    return {
      tweaks: parsed.tweaks as Record<string, unknown>,
      creatorData:
        parsed.creatorData && typeof parsed.creatorData === "object"
          ? (parsed.creatorData as Record<string, unknown>)
          : {},
    };
  } catch {
    return null;
  }
}

export function clearWebsiteUnpublishedDraft(userId: string) {
  const uid = String(userId || "").trim();
  if (typeof window === "undefined" || !uid) return;
  try {
    window.sessionStorage.removeItem(unpublishedDraftKey(uid));
  } catch {
    /* ignore */
  }
}

/**
 * On editor mount: re-bind sessionStorage so unpublished Add/Edit work
 * survives SPA remounts and browser refresh. Refresh no longer silently
 * hard-deletes drafts — the editor prompts Discard vs Publish instead.
 */
export async function maybeAbandonWebsiteTemplateSessionOnLoad(
  userId: string,
) {
  bindWebsiteTemplateSession(userId);
}

/**
 * On editor mount after a refresh: drop abandoned session drafts so they
 * never leak into a later Publish, then clear sessionStorage.
 * @deprecated Prefer maybeAbandonWebsiteTemplateSessionOnLoad — refresh now
 * keeps drafts and prompts Discard/Publish in the editor.
 */
export async function abandonWebsiteTemplateSessionOnLoad(userId: string) {
  await discardWebsiteTemplateSession(userId);
}

function parseDetailsObject(raw: unknown): Record<string, unknown> {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw !== "string" || !raw.trim()) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

/** After Publish writes campaign rows, copy those details onto creators_campaigns. */
async function syncCreatorsCampaignsFromTemplateRows(
  userId: string,
  ids: string[],
) {
  const unique = [...new Set(ids.map(rowId).filter(Boolean))];
  if (!unique.length) return;
  const { data, error } = await supabase
    .from(TEMPLATE_COMPONENTS_TABLE)
    .select("id,type,details")
    .eq("user_id", userId)
    .in("id", unique);
  if (error || !data?.length) return;
  for (const row of data as Array<{ type?: string; details?: unknown }>) {
    if (String(row.type || "") !== "campaign") continue;
    const details = parseDetailsObject(row.details);
    const campaignId = String(details.campaign_id || "").trim();
    if (!campaignId) continue;
    const name = String(details.campaign_name || "").trim();
    const button = String(details.campaign_button_text || "").trim();
    const image = String(details.image || "").trim();
    const { data: existing } = await supabase
      .from("creators_campaigns")
      .select("metadata")
      .eq("id", campaignId)
      .eq("user_id", userId)
      .maybeSingle();
    const prevMeta =
      existing?.metadata &&
      typeof existing.metadata === "object" &&
      !Array.isArray(existing.metadata)
        ? (existing.metadata as Record<string, unknown>)
        : {};
    const patch: Record<string, unknown> = {
      metadata: {
        ...prevMeta,
        ...(Array.isArray(details.fields) ? { fields: details.fields } : {}),
      },
    };
    if (name) patch.campaign_name = name;
    if (button) patch.campaign_button_text = button;
    if (image && !image.startsWith("blob:") && !image.startsWith("data:")) {
      patch.image = image;
    }
    const imageZoom = Number(details.imageZoom);
    const imageX = Number(details.imageX);
    const imageY = Number(details.imageY);
    if (
      Number.isFinite(imageZoom) ||
      Number.isFinite(imageX) ||
      Number.isFinite(imageY)
    ) {
      patch.metadata = {
        ...(patch.metadata as Record<string, unknown>),
        ...(Number.isFinite(imageZoom) ? { imageZoom } : {}),
        ...(Number.isFinite(imageX) ? { imageX } : {}),
        ...(Number.isFinite(imageY) ? { imageY } : {}),
      };
    }
    await supabase
      .from("creators_campaigns")
      .update(patch)
      .eq("id", campaignId)
      .eq("user_id", userId);
  }
}

/**
 * Publish: activate session drafts, flush deferred patches, hard-delete
 * deferred removals.
 */
export async function publishWebsiteTemplateSession(
  userId: string,
): Promise<boolean> {
  bindWebsiteTemplateSession(userId);
  const uid = String(userId || "").trim();
  if (!uid) return false;

  const created = [...createdIds];
  const deleted = [...deletedIds];
  const dirty = [...dirtyPatches.entries()];

  try {
    if (created.length) {
      await supabase
        .from(TEMPLATE_COMPONENTS_TABLE)
        .update({ status: "active", status_expiry: "active" })
        .eq("user_id", uid)
        .in("id", created);
    }

    const publishedIds = [...created];
    for (const [id, patch] of dirty) {
      if (deleted.includes(id)) continue;
      const { error } = await supabase
        .from(TEMPLATE_COMPONENTS_TABLE)
        .update(patch)
        .eq("user_id", uid)
        .eq("id", id);
      if (error) throw error;
      publishedIds.push(id);
    }

    await syncCreatorsCampaignsFromTemplateRows(uid, publishedIds);

    if (deleted.length) {
      await hardDeleteTemplateComponentIds(uid, deleted);
    }

    createdIds.clear();
    deletedIds.clear();
    dirtyPatches.clear();
    createdUserLinkIds.clear();
    linkSessionCollections.clear();
    clearStorage();
    return true;
  } catch {
    return false;
  }
}

/** Compat: update a `user_links` row (immediate write; draft sections stay private). */
export async function queueWebsiteUserLinkUpdate(
  userId: string,
  linkId: unknown,
  patch: Record<string, unknown>,
) {
  const uid = String(userId || "").trim() || storageUserId;
  const id = rowId(linkId);
  if (!uid || !id || !patch || Object.keys(patch).length === 0) return;
  const { error } = await supabase
    .from("user_links")
    .update(patch)
    .eq("user_id", uid)
    .eq("id", id);
  if (error) throw error;
  markSessionDirty();
}

/** Compat: hard-delete a `user_links` row. */
export async function queueWebsiteUserLinkDelete(
  userId: string,
  linkId: unknown,
) {
  const uid = String(userId || "").trim() || storageUserId;
  const id = rowId(linkId);
  if (!uid || !id) return;
  const { error } = await supabase
    .from("user_links")
    .delete()
    .eq("user_id", uid)
    .eq("id", id);
  if (error) throw error;
  createdUserLinkIds.delete(id);
  markSessionDirty();
}
